import categoriesData from "../img/categories.json";
import { slugify } from "../components/MenuCard";

/* ----------------------------
   Normalize Category (keeps existing shapes compatible)
   simple, robust normalizer used for sidebar + links
----------------------------- */
const normalizeCategory = (raw = {}, idx = 0) => {
  const safeStr = (v) => (v == null ? "" : typeof v === "string" ? v : String(v));
  // swiggy-like with itemCards
  if (Array.isArray(raw.itemCards)) {
    const items = raw.itemCards
      .map((c) => c?.card?.info)
      .filter(Boolean)
      .map((info, i) => {
        const imageRel = info.imageRelPath || info.imageId || info.image || "";
        const img = Array.isArray(imageRel) ? String(imageRel[0] || "") : String(imageRel || "");
        const file = img ? img.split("/").pop() : "";
        const price = typeof info.price === "number" ? info.price / 100 : Number(String(info.priceString || info.price || 0).replace(/[^\d.]/g, "")) || 0;
        return {
          id: String(info.id ?? `item-${i}`),
          name: safeStr(info.name),
          description: safeStr(info.description || info.subtitle),
          img,
          imgFile: file,
          price,
          nutrition: info.nutrition || {},
          type: info.itemAttribute?.vegClassifier === "NONVEG" ? "nonveg" : info.isVeg ? "veg" : undefined,
        };
      });
    const name = safeStr(raw.title || raw.name || `Category ${idx + 1}`);
    return { id: raw.id ?? slugify(name), name, items, image48Id: raw.image48Id || raw.image || raw.img || "" };
  }

  // already normalized (items/menu)
  if (Array.isArray(raw.items) || Array.isArray(raw.menu)) {
    const arr = Array.isArray(raw.items) ? raw.items : raw.menu;
    const items = arr.map((it, i) => {
      const imageRel = it.imageRelPath || it.imageId || it.image || it.img || "";
      const img = Array.isArray(imageRel) ? String(imageRel[0] || "") : String(imageRel || "");
      const file = img ? img.split("/").pop() : "";
      return {
        id: String(it.id ?? `item-${i}`),
        name: safeStr(it.name || it.title),
        description: safeStr(it.description || it.subtitle),
        img,
        imgFile: file,
        price: Number(it.price) || (it.priceInPaise ? it.priceInPaise / 100 : 0) || 0,
        nutrition: it.nutrition || {},
        type: it.type || (it.isVeg ? "veg" : undefined),
      };
    });
    const name = safeStr(raw.name || raw.title || `Category ${idx + 1}`);
    return { id: raw.id ?? slugify(name), name, items, image48Id: raw.image48Id || raw.image || raw.img || "" };
  }

  // object map single key { "Salads": [...] }
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const keys = Object.keys(raw);
    if (keys.length === 1 && Array.isArray(raw[keys[0]])) {
      const name = keys[0];
      const items = raw[name].map((it, i) => {
        const imageRel = it.imageRelPath || it.imageId || it.image || it.img || "";
        const img = Array.isArray(imageRel) ? String(imageRel[0] || "") : String(imageRel || "");
        const file = img ? img.split("/").pop() : "";
        return {
          id: String(it.id ?? `item-${i}`),
          name: safeStr(it.name || it.title),
          description: safeStr(it.description),
          img,
          imgFile: file,
          price: Number(it.price) || 0,
          nutrition: it.nutrition || {},
          type: it.type,
        };
      });
      return { id: slugify(name), name, items, image48Id: "" };
    }
  }

  // fallback
  const name = safeStr(raw.title || raw.name || `Category ${idx + 1}`);
  return { id: raw.id ?? slugify(name), name, items: Array.isArray(raw) ? raw : [], image48Id: raw.image48Id || raw.image || raw.img || "" };
};

/* ----------------------------
   Normalize all categories (flatten parent.categories)
----------------------------- */
const normalizeCategories = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) {
    return data.flatMap((entry, i) => {
      if (Array.isArray(entry.categories)) {
        return entry.categories.map((sub, j) => {
          const cat = normalizeCategory(sub, `${i}-${j}`);
          cat.parent = entry.title || "";
          cat.id = cat.id ?? `${slugify(entry.title || `parent-${i}`)}--${slugify(sub.title || sub.name || `sub-${j}`)}`;
          cat.image48Id = cat.image48Id || entry.image48Id || "";
          return cat;
        });
      }
      return [normalizeCategory(entry, i)];
    });
  }
  if (typeof data === "object") {
    return Object.entries(data).map(([k, v], idx) => {
      if (Array.isArray(v)) return { id: slugify(k), name: k, items: v, image48Id: "" };
      return normalizeCategory({ name: k, ...v }, idx);
    });
  }
  return [];
};

/* ----------------------------
   Build categories
----------------------------- */
export const categories = normalizeCategories(categoriesData);
const firstWithItems = categories.find((c) => c.items?.length > 0);
export const defaultKey = firstWithItems?.id || categories[0]?.id || "default";
